import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Rocket, X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { PROMO_NAV } from "../nav";
import { ConnectionStrip } from "./ConnectionHealth";

export function PromotionSidebar({ mobileOpen, onClose }) {
  const { t } = useTranslation();

  const nav = (
    <nav className="flex-1 space-y-0.5 overflow-y-auto p-3" data-testid="promo-nav">
      {PROMO_NAV.map(({ to, key, labelKey, Icon, end, soon, section }) => {
        const item = soon ? (
          <div
            key={key}
            className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground/60"
            data-testid={`promo-nav-${key}`}
            title={t("promo.nav.soon")}
          >
            <Icon size={18} weight="duotone" />
            <span className="flex-1">{t(labelKey)}</span>
            <span className="rounded-full border border-border px-1.5 py-px text-[9px] uppercase tracking-wider">
              {t("promo.nav.soon")}
            </span>
          </div>
        ) : (
          <NavLink
            key={key}
            to={to}
            end={end}
            onClick={onClose}
            data-testid={`promo-nav-${key}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                isActive
                  ? "border border-brand/25 bg-brand/10 font-medium text-brand"
                  : "border border-transparent text-foreground/80 hover:bg-accent/60 hover:text-foreground",
              )
            }
          >
            <Icon size={18} weight="duotone" />
            {t(labelKey)}
          </NavLink>
        );
        if (section) {
          return (
            <div key={`sec-${key}`}>
              <div className="mb-1 mt-4 border-t border-border/60 pt-3 promo-overline px-3" data-testid={`promo-section-${section}`}>
                {t(`promo.nav.sections.${section}`)}
              </div>
              {item}
            </div>
          );
        }
        return item;
      })}
    </nav>
  );

  const inner = (
    <>
      <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-border px-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <Rocket size={18} weight="fill" />
        </div>
        <div className="leading-none">
          <div className="font-heading text-sm font-medium tracking-tight">Promotion Center</div>
          <div className="promo-overline mt-1">GrowthAI</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-accent md:hidden"
            data-testid="promo-sidebar-close"
          >
            <X size={18} />
          </button>
        )}
      </div>
      {nav}
      <div className="shrink-0 border-t border-border p-4">
        <div className="promo-overline mb-2.5">{t("promo.conn.title")}</div>
        <ConnectionStrip />
      </div>
    </>
  );

  return (
    <>
      {/* Desktop */}
      <aside
        className="hidden w-64 shrink-0 flex-col border-r border-border bg-card md:flex"
        data-testid="promo-sidebar"
      >
        {inner}
      </aside>

      {/* Mobile drawer (in-scope, no portal) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="promo-sidebar-mobile">
          <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onClose} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col border-r border-border bg-card">
            {inner}
          </aside>
        </div>
      )}
    </>
  );
}
